import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
  type ColumnDef,
} from '@tanstack/react-table';
import { MainLayout } from '../layouts';
import { api, type EscalationWithDetails, type EscalationStatus } from '../lib/api';

// Simple time-ago formatter
const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  
  const intervals: { [key: string]: number } = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
  };
  
  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
    }
  }
  
  return 'just now';
};

export const ExpertDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<EscalationStatus | 'all'>('all');
  const [sorting, setSorting] = useState<SortingState>([]);

  // Poll for escalations every 30 seconds
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['escalations', statusFilter],
    queryFn: async () => {
      const response = await api.getEscalations({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
        offset: 0,
      });
      return response;
    },
    refetchInterval: 30000, // Poll every 30 seconds
  });

  const handleClaim = async (escalationId: string) => {
    try {
      await api.claimEscalation(escalationId);
      refetch(); // Refresh data after claiming
    } catch (error) {
      console.error('Failed to claim escalation:', error);
      alert('Failed to claim escalation. Please try again.');
    }
  };

  const handleViewDetails = (escalationId: string) => {
    navigate(`/expert-dashboard/${escalationId}`);
  };

  // The table is wider than most viewports, so the Actions column (Claim/View)
  // is pinned to the right edge instead of scrolling out of sight.
  const PINNED_ACTIONS_HEADER =
    'sticky right-0 bg-gray-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]';
  const PINNED_ACTIONS_CELL =
    'sticky right-0 bg-white group-hover:bg-gray-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]';

  const columns: ColumnDef<EscalationWithDetails>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: (info) => (
        <span className="text-xs font-mono text-gray-500">
          {info.getValue<string>().substring(0, 8)}...
        </span>
      ),
    },
    {
      accessorKey: 'user.name',
      header: 'User',
      cell: (info) => {
        const user = info.row.original.user;
        const userName = user?.displayName || user?.name ;
        const contact = user?.phone || user?.email;
        const org = user?.organization;
        return (
          <div>
            <div className="font-medium">{userName}</div>
            <div className="text-xs text-gray-500">{contact}</div>
            {org && <div className="text-xs text-gray-400 italic">{org}</div>}
          </div>
        );
      },
    },
    {
      accessorKey: 'conversation.title',
      header: 'Conversation',
      cell: (info) => (
        <span className="truncate max-w-[14rem] block" title={info.getValue<string>()}>
          {info.getValue<string>()}
        </span>
      ),
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: (info) => {
        const priority = info.getValue<string>();
        const colorMap: Record<string, string> = {
          low: 'bg-gray-100 text-gray-800',
          normal: 'bg-blue-100 text-blue-800',
          high: 'bg-orange-100 text-orange-800',
          urgent: 'bg-red-100 text-red-800',
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              colorMap[priority] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {priority}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const status = info.getValue<string>();
        const colorMap: Record<string, string> = {
          pending: 'bg-yellow-100 text-yellow-800',
          in_progress: 'bg-blue-100 text-blue-800',
          resolved: 'bg-green-100 text-green-800',
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              colorMap[status] || 'bg-gray-100 text-gray-800'
            }`}
          >
            {status.replace('_', ' ')}
          </span>
        );
      },
    },
    {
      accessorKey: 'escalatedAt',
      header: 'Escalated',
      cell: (info) => (
        <span className="text-sm text-gray-600">
          {formatTimeAgo(new Date(info.getValue<string>()))}
        </span>
      ),
    },
    {
      accessorKey: 'assignedExpert',
      header: 'Assigned To',
      cell: (info) => {
        const expert = info.row.original.assignedExpert;
        return expert ? (
          <span className="text-sm">{expert.name}</span>
        ) : (
          <span className="text-sm text-gray-400 italic">Unassigned</span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const row = info.row.original;
        const isPending = row.status === 'pending';
        const isInProgress = row.status === 'in_progress';

        return (
          <div className="flex gap-2">
            {isPending && (
              <button
                onClick={() => handleClaim(row.id)}
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
              >
                Claim
              </button>
            )}
            {(isInProgress || row.status === 'resolved') && (
              <button
                onClick={() => handleViewDetails(row.id)}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
              >
                View
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: data?.escalations || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading escalations...</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error loading escalations: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </div>
      </MainLayout>
    );
  }

  const escalations = data?.escalations || [];
  const pendingCount = escalations.filter((e) => e.status === 'pending').length;
  const inProgressCount = escalations.filter((e) => e.status === 'in_progress').length;
  const resolvedCount = escalations.filter((e) => e.status === 'resolved').length;

  return (
    <MainLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Expert Dashboard</h1>
          <p className="text-gray-600">Manage customer escalations and expert requests</p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="text-sm font-medium text-gray-600">Total Escalations</div>
            <div className="text-3xl font-bold text-gray-900 mt-2">{escalations.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border border-yellow-200">
            <div className="text-sm font-medium text-yellow-700">Pending</div>
            <div className="text-3xl font-bold text-yellow-600 mt-2">{pendingCount}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border border-blue-200">
            <div className="text-sm font-medium text-blue-700">In Progress</div>
            <div className="text-3xl font-bold text-blue-600 mt-2">{inProgressCount}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border border-green-200">
            <div className="text-sm font-medium text-green-700">Resolved</div>
            <div className="text-3xl font-bold text-green-600 mt-2">{resolvedCount}</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-lg shadow mb-4">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {(['all', 'pending', 'in_progress', 'resolved'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-6 py-3 text-sm font-medium border-b-2 ${
                    statusFilter === status
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {status === 'all' ? 'All' : status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Escalations Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 ${
                          header.column.id === 'actions' ? PINNED_ACTIONS_HEADER : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      No escalations found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="group hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={`px-4 py-4 whitespace-nowrap ${
                            cell.column.id === 'actions' ? PINNED_ACTIONS_CELL : ''
                          }`}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Info */}
        {data && data.pagination.total > 0 && (
          <div className="mt-4 text-sm text-gray-600 text-center">
            Showing {data.escalations.length} of {data.pagination.total} escalations
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ExpertDashboard;
